"use client";

import { useState } from "react";
import { toProxiedWpUrl } from "@utils/imageUrl";
import HeaderPublic from "@components/layout/HeaderPublic";
import FooterPublic from "@components/layout/FooterPublic";
import ArticleLayout from "@components/services/ArticleLayout";
import ArticleTOC from "@components/services/ArticleTOC";
import AnswerCard from "@components/services/AnswerCard";
import KeyFactsBar from "@components/services/KeyFactsBar";
import ExpertQuote from "@components/services/ExpertQuote";
import FAQList from "@components/services/FAQList";
import ScopeGrid from "@components/services/ScopeGrid";
import SectionHead from "@components/services/SectionHead";
import ServiceHero from "@components/services/ServiceHero";
import ClientLogosMarquee from "@components/sections/ClientLogosMarquee";
import TimelineSteps from "@components/services/TimelineSteps";
import WhitepaperDownloadForm from "@components/features/whitepaper/WhitepaperDownloadForm";
import pageData from "@data/landing-pages/ki-governance-agentic-bi.json";
import type { HeroData } from "@components/services/types";
import type { ReactNode } from "react";

interface BasicCardItem {
  title: string;
  description: string;
}

interface KiGovernancePageData {
  hero: HeroData;
  answer: {
    eyebrow: string;
    title: string;
    paragraphs: string[];
    keyfacts?: { label: string; sublabel: string }[];
  };
  challenge: {
    eyebrow: string;
    title: string;
    description: string;
    items: BasicCardItem[];
  };
  transformation: {
    eyebrow: string;
    title: string;
    description: string;
    steps: Array<{ phase: string; duration: string; title: string; description: string }>;
  };
  approach: {
    eyebrow: string;
    title: string;
    description: string;
    intro: string;
    layers: BasicCardItem[];
    visual: {
      src: string;
      alt: string;
      caption: string;
    };
  };
  demo: {
    eyebrow: string;
    title: string;
    tagline: string;
    videoUrl: string;
    videoAlt: string;
    steps: Array<{ number: string; title: string; description: string }>;
  };
  servicesPhases: {
    eyebrow: string;
    title: string;
    description: string;
    variants: {
      title: string;
      items: Array<{ label: string; description: string }>;
    };
    phases: BasicCardItem[];
  };
  whyDbi: {
    eyebrow: string;
    title: string;
    items: BasicCardItem[];
  };
  competitive: {
    eyebrow: string;
    title: string;
    intro: string;
    competitors: Array<{ name: string; category: string; approach: string; limitation: string }>;
    columns: string[];
    rows: Array<{
      criterion: string;
      description: string;
      values: Array<{ text: string; tier: "strong" | "medium" | "weak" }>;
    }>;
    footnote: string;
  };
  technology: {
    eyebrow: string;
    title: string;
    description: string;
    connectors: Array<{ name: string; description: string }>;
  };
  trust: {
    eyebrow: string;
    title: string;
    description: string;
  };
  faq: {
    eyebrow: string;
    title: string;
    faqs: Array<{ question: string; answer: string }>;
  };
  closing: {
    eyebrow: string;
    title: string;
    description: string;
  };
  whitepaperCta: {
    title: string;
    description: string;
  };
  closingContact: {
    quote: string;
    author: string;
    role: string;
    phone?: string;
    imagePath: string;
    ctaText: string;
    ctaHref: string;
    authorHref: string;
    authorLinkedIn: string;
  };
}


const tocItems = [
  { id: "tldr", label: "TL;DR" },
  { id: "herausforderung", label: "Herausforderung" },
  { id: "ansatz", label: "Ansatz" },
  { id: "transformation", label: "Transformation" },
  { id: "demo", label: "Live Demo" },
  { id: "phasen", label: "Phasen" },
  { id: "warum-dbi", label: "Warum DBI" },
  { id: "wettbewerb", label: "Vergleich" },
  { id: "technologie", label: "Technologie" },
  { id: "trust", label: "Kundenlogos" },
  { id: "faq", label: "Kosten & FAQ" },
  { id: "abschluss", label: "Abschluss" }
];

interface KiGovernanceClientProps {
  /** GuideReadNext for /services/ki-governance-agentic-bi-beratung (the canonical
   *  route; /services/ki-governance-agentic-bi 301s there), rendered by the server page. */
  guideSlot: ReactNode;
}

export default function KiGovernanceClient({ guideSlot }: KiGovernanceClientProps) {
  const data = pageData as KiGovernancePageData;
  const [activeConnector, setActiveConnector] = useState<string | null>(null);

  const selectedConnector = data.technology.connectors.find(
    (c) => c.name === activeConnector
  );

  return (
    <>
      <HeaderPublic />
      <ServiceHero hero={data.hero} />

      <ArticleLayout toc={<ArticleTOC items={tocItems} />}>
        <section id="tldr" style={{ marginTop: 64 }}>
          <SectionHead eyebrow={data.answer.eyebrow} title={data.answer.title} blue />
          <AnswerCard paragraphs={data.answer.paragraphs} />
          <KeyFactsBar facts={data.answer.keyfacts} />
        </section>

        <section id="herausforderung" style={{ marginTop: 64 }}>
          <SectionHead
            eyebrow={data.challenge.eyebrow}
            title={data.challenge.title}
            lede={data.challenge.description}
            blue
          />
          <ScopeGrid
            items={data.challenge.items.map((item) => ({
              title: item.title,
              description: item.description,
            }))}
          />
        </section>

        <section id="ansatz" style={{ marginTop: 64 }}>
          <SectionHead
            eyebrow={data.approach.eyebrow}
            title={data.approach.title}
            lede={data.approach.description}
            blue
          />
          <figure
            style={{
              marginTop: 24,
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <img
              src={data.approach.visual.src}
              alt={data.approach.visual.alt}
              width={1280}
              height={675}
              style={{ width: "100%", height: "auto", objectFit: "cover", display: "block" }}
              loading="lazy"
              decoding="async"
            />
            <figcaption
              style={{
                padding: "12px 20px",
                fontSize: 13,
                color: "var(--fg-3)",
              }}
            >
              {data.approach.visual.caption}
            </figcaption>
          </figure>
          <div className="service-answer-card mt-6">
            <p>{data.approach.intro}</p>
          </div>
          <ScopeGrid
            items={data.approach.layers.map((layer) => ({
              title: layer.title,
              description: layer.description,
            }))}
          />
        </section>

        <section id="transformation" style={{ marginTop: 64 }}>
          <SectionHead
            eyebrow={data.transformation.eyebrow}
            title={data.transformation.title}
            lede={data.transformation.description}
            blue
          />
          <TimelineSteps steps={data.transformation.steps} className="timeline-spacious" />
        </section>

        <section id="demo" style={{ marginTop: 64 }}>
          <SectionHead
            eyebrow={data.demo.eyebrow}
            title={data.demo.title}
            lede={data.demo.tagline}
            blue
          />
          {/* Video — proxied through /api/wp-media to avoid mixed-content blocking on HTTPS */}
          <div className="demo-video-wrapper">
            <video
              autoPlay
              muted
              loop
              controls
              playsInline
              preload="metadata"
              aria-label={data.demo.videoAlt}
              className="demo-video"
            >
              <source src={toProxiedWpUrl(data.demo.videoUrl)} type="video/mp4" />
            </video>
          </div>
          <ol className="demo-steps">
            {data.demo.steps.map((step) => (
              <li key={step.number} className="demo-step">
                <span className="demo-step-number" aria-hidden="true">{step.number}</span>
                <div className="demo-step-body">
                  <h3 className="demo-step-title">{step.title}</h3>
                  <p className="demo-step-desc">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
          <style jsx>{`
            .demo-video-wrapper {
              margin-top: 24px;
              border-radius: 8px;
              overflow: hidden;
              border: 1px solid var(--border);
              background: #000;
            }
            .demo-video {
              width: 100%;
              height: auto;
              display: block;
            }
            .demo-steps {
              margin: 20px 0 0;
              padding: 0;
              list-style: none;
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 12px;
            }
            .demo-step {
              display: flex;
              gap: 12px;
              align-items: flex-start;
              background: #fff;
              border: 1px solid var(--border);
              border-radius: var(--radius-xl);
              padding: 14px 16px;
            }
            .demo-step-number {
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 28px;
              height: 28px;
              border-radius: 9999px;
              background: var(--color-accent, #f59e0b);
              color: #fff;
              font-size: 12px;
              font-weight: 700;
              line-height: 1;
            }
            .demo-step-body {
              min-width: 0;
            }
            .demo-step-title {
              margin: 0 0 4px;
              font-size: 0.9rem;
              font-weight: 700;
              color: var(--fg-1);
            }
            .demo-step-desc {
              margin: 0;
              font-size: 0.85rem;
              color: var(--fg-2);
              line-height: 1.5;
            }
            @media (max-width: 640px) {
              .demo-steps {
                grid-template-columns: 1fr;
              }
            }
          `}</style>
        </section>

        <section id="phasen" style={{ marginTop: 64 }}>
          <SectionHead
            eyebrow={data.servicesPhases.eyebrow}
            title={data.servicesPhases.title}
            lede={data.servicesPhases.description}
            blue
          />
          <div className="variant-grid">
            <h3 className="variant-grid-title">{data.servicesPhases.variants.title}</h3>
            <ol className="variant-list">
              {data.servicesPhases.variants.items.map((v) => (
                <li key={v.label} className="variant-item">
                  <strong className="variant-label">{v.label}</strong>
                  <p className="variant-desc">{v.description}</p>
                </li>
              ))}
            </ol>
          </div>
          <ScopeGrid
            items={data.servicesPhases.phases.map((phase) => ({
              title: phase.title,
              description: phase.description,
            }))}
          />
          <style jsx>{`
            .variant-grid {
              margin-top: 24px;
              margin-bottom: 8px;
              padding: 24px 28px;
              background: var(--color-grey-50);
              border: 1px solid var(--border);
              border-radius: var(--radius-xl);
            }
            .variant-grid-title {
              font-family: var(--font-headline);
              font-size: 16px;
              font-weight: 600;
              color: var(--color-corporate-blue);
              margin: 0 0 16px;
            }
            .variant-list {
              margin: 0;
              padding: 0;
              list-style: none;
              display: flex;
              flex-direction: column;
              gap: 14px;
            }
            .variant-item {
              display: flex;
              flex-direction: column;
              gap: 4px;
            }
            .variant-label {
              font-size: 14px;
              font-weight: 700;
              color: var(--color-corporate-orange);
            }
            .variant-desc {
              font-size: 14px;
              line-height: 1.55;
              color: var(--fg-2);
              margin: 0;
              max-width: 72ch;
            }
          `}</style>
        </section>

        <section id="warum-dbi" style={{ marginTop: 64 }}>
          <SectionHead eyebrow={data.whyDbi.eyebrow} title={data.whyDbi.title} blue />
          <ScopeGrid
            items={data.whyDbi.items.map((item) => ({
              title: item.title,
              description: item.description,
            }))}
          />
        </section>

        <section id="wettbewerb" style={{ marginTop: 64 }}>
          <SectionHead
            eyebrow={data.competitive.eyebrow}
            title={data.competitive.title}
            lede={data.competitive.intro}
            blue
          />
          {/* Competitor context cards */}
          <div className="comp-cards">
            {data.competitive.competitors.map((c) => (
              <div key={c.name} className="comp-card">
                <div className="comp-card-header">
                  <span className="comp-card-name">{c.name}</span>
                  <span className="comp-card-category">{c.category}</span>
                </div>
                <p className="comp-card-approach">{c.approach}</p>
                <p className="comp-card-limit">
                  <span className="comp-card-limit-label">Einschränkung: </span>
                  {c.limitation}
                </p>
              </div>
            ))}
          </div>
          {/* Comparison table */}
          <div className="comp-table-wrap">
            <table className="comp-table" aria-label="ABIS Wettbewerbsvergleich">
              <thead>
                <tr>
                  <th className="comp-th comp-th-criterion" scope="col">Kriterium</th>
                  {data.competitive.columns.map((col, i) => (
                    <th
                      key={col}
                      scope="col"
                      className={`comp-th${i === 0 ? " comp-th-abis" : " comp-th-other"}`}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.competitive.rows.map((row) => (
                  <tr key={row.criterion} className="comp-tr">
                    <td className="comp-td-criterion">
                      <span className="comp-criterion-label">{row.criterion}</span>
                      <span className="comp-criterion-desc">{row.description}</span>
                    </td>
                    {row.values.map((val, i) => (
                      <td
                        key={i}
                        className={`comp-td${i === 0 ? " comp-td-abis" : ""} comp-td--${val.tier}`}
                      >
                        <span className="comp-tier-icon" aria-hidden>
                          {val.tier === "strong" ? "✓" : val.tier === "weak" ? "–" : "~"}
                        </span>
                        <span className="comp-td-text">{val.text}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="comp-footnote">{data.competitive.footnote}</p>
          <style jsx>{`
            .comp-cards {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
              margin-top: 24px;
              margin-bottom: 28px;
            }
            @media (max-width: 768px) {
              .comp-cards { grid-template-columns: 1fr; }
            }
            .comp-card {
              padding: 16px 18px;
              background: #fff;
              border: 1px solid var(--border);
              border-radius: var(--radius-xl);
            }
            .comp-card-header {
              display: flex;
              align-items: baseline;
              gap: 8px;
              margin-bottom: 8px;
            }
            .comp-card-name {
              font-family: var(--font-headline);
              font-size: 14px;
              font-weight: 700;
              color: var(--fg-1);
            }
            .comp-card-category {
              font-size: 11px;
              font-weight: 600;
              letter-spacing: 0.05em;
              text-transform: uppercase;
              color: var(--fg-3);
            }
            .comp-card-approach {
              font-size: 13px;
              line-height: 1.55;
              color: var(--fg-2);
              margin: 0 0 8px;
            }
            .comp-card-limit {
              font-size: 12px;
              line-height: 1.5;
              color: var(--fg-3);
              margin: 0;
            }
            .comp-card-limit-label {
              font-weight: 600;
              color: var(--fg-2);
            }
            .comp-table-wrap {
              overflow-x: auto;
              border-radius: var(--radius-xl);
              border: 1px solid var(--border);
            }
            .comp-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
            }
            .comp-th {
              padding: 12px 14px;
              font-size: 12px;
              font-weight: 700;
              letter-spacing: 0.04em;
              text-align: left;
              white-space: nowrap;
              border-bottom: 2px solid var(--border);
            }
            .comp-th-criterion {
              background: var(--color-grey-50);
              color: var(--fg-3);
              min-width: 160px;
            }
            .comp-th-abis {
              background: var(--color-corporate-blue);
              color: #fff;
              min-width: 160px;
            }
            .comp-th-other {
              background: var(--color-grey-50);
              color: var(--fg-2);
              min-width: 150px;
            }
            .comp-tr:not(:last-child) td {
              border-bottom: 1px solid var(--border);
            }
            .comp-tr:hover td {
              background: var(--color-grey-50);
            }
            .comp-tr:hover .comp-td-abis {
              background: #1a3a6b;
            }
            .comp-td-criterion {
              padding: 12px 14px;
              vertical-align: top;
              background: #fafafa;
            }
            .comp-criterion-label {
              display: block;
              font-weight: 700;
              color: var(--fg-1);
              margin-bottom: 2px;
            }
            .comp-criterion-desc {
              display: block;
              font-size: 11px;
              color: var(--fg-3);
            }
            .comp-td {
              padding: 12px 14px;
              vertical-align: middle;
              background: #fff;
            }
            .comp-td-abis {
              background: #1e3f73;
              color: #fff;
            }
            .comp-td--strong .comp-tier-icon { color: #22c55e; }
            .comp-td--medium .comp-tier-icon { color: #f59e0b; }
            .comp-td--weak   .comp-tier-icon { color: #ef4444; }
            .comp-td-abis .comp-tier-icon { color: #86efac; }
            .comp-tier-icon {
              font-size: 14px;
              font-weight: 700;
              margin-right: 6px;
            }
            .comp-td-text {
              font-size: 13px;
              line-height: 1.4;
            }
            .comp-td-abis .comp-td-text {
              color: #fff;
              font-weight: 600;
            }
            .comp-footnote {
              margin-top: 10px;
              font-size: 11px;
              color: var(--fg-3);
              line-height: 1.5;
            }
          `}</style>
        </section>

        <section id="technologie" style={{ marginTop: 64 }}>
          <SectionHead
            eyebrow={data.technology.eyebrow}
            title={data.technology.title}
            lede={data.technology.description}
            blue
          />
          <div className="trust-band">
            <h3 className="trust-band-heading">Systeme und Modelle im Setup</h3>
            <p className="trust-band-hint">Klicken Sie auf ein System um zu sehen, wie ABIS es anbindet.</p>
            <ul className="trust-band-grid" role="list">
              {data.technology.connectors.map((connector) => {
                const isActive = activeConnector === connector.name;
                return (
                  <li key={connector.name}>
                    <button
                      className={`trust-band-item${isActive ? " trust-band-item--active" : ""}`}
                      onClick={() =>
                        setActiveConnector(isActive ? null : connector.name)
                      }
                      aria-expanded={isActive ? "true" : "false"}
                      aria-controls={`connector-desc-${connector.name.replace(/\s+/g, "-")}`}
                      type="button"
                    >
                      <span className="trust-band-dot" aria-hidden />
                      <span className="trust-band-label">{connector.name}</span>
                      <span className="trust-band-chevron" aria-hidden>
                        {isActive ? "▲" : "▼"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {selectedConnector && (
              <div
                id={`connector-desc-${selectedConnector.name.replace(/\s+/g, "-")}`}
                className="connector-description"
                role="region"
                aria-label={`ABIS-Integration: ${selectedConnector.name}`}
              >
                <strong className="connector-description-name">{selectedConnector.name}</strong>
                <p className="connector-description-text">{selectedConnector.description}</p>
              </div>
            )}
          </div>
          <style jsx>{`
            .trust-band-hint {
              font-size: 13px;
              color: var(--fg-3);
              margin: -4px 0 16px;
            }
            .trust-band-item {
              display: flex;
              align-items: center;
              gap: 6px;
              cursor: pointer;
              background: none;
              border: 1px solid var(--border);
              border-radius: var(--radius-lg);
              padding: 6px 12px 6px 10px;
              font-size: 13px;
              font-weight: 500;
              color: var(--fg-1);
              white-space: nowrap;
              transition: background 0.15s, border-color 0.15s, color 0.15s;
              width: 100%;
            }
            .trust-band-item:hover:not(.trust-band-item--active) {
              background: var(--color-grey-50);
              border-color: var(--color-corporate-blue);
              color: var(--fg-1);
            }
            .trust-band-item:hover:not(.trust-band-item--active) .trust-band-label {
              color: var(--fg-1);
            }
            .trust-band-item:hover:not(.trust-band-item--active) .trust-band-chevron {
              color: var(--fg-1);
              opacity: 0.75;
            }
            .trust-band-item--active:hover {
              background: var(--color-corporate-blue);
              border-color: var(--color-corporate-blue);
              color: #fff;
            }
            .trust-band-item--active:hover .trust-band-label,
            .trust-band-item--active:hover .trust-band-chevron {
              color: #fff;
            }
            .trust-band-item--active {
              background: var(--color-corporate-blue);
              border-color: var(--color-corporate-blue);
              color: #fff;
            }
            /* Global services.css sets .trust-band-label { color: corporate-blue } — override when active */
            .trust-band-item--active .trust-band-label {
              color: #fff;
            }
            .trust-band-item--active .trust-band-dot {
              background: rgba(255, 255, 255, 0.6);
            }
            .trust-band-chevron {
              margin-left: auto;
              font-size: 9px;
              opacity: 0.6;
            }
            .trust-band-item--active .trust-band-chevron {
              color: #fff;
              opacity: 1;
            }
            .connector-description {
              margin-top: 16px;
              padding: 18px 20px;
              background: var(--color-corporate-blue);
              color: #fff;
              border-radius: var(--radius-xl);
              animation: fadeIn 0.18s ease;
            }
            .connector-description-name {
              display: block;
              font-size: 13px;
              font-weight: 700;
              letter-spacing: 0.04em;
              text-transform: uppercase;
              color: rgba(255, 255, 255, 0.85);
              margin-bottom: 6px;
            }
            .connector-description-text {
              margin: 0;
              font-size: 14px;
              line-height: 1.65;
              color: #fff;
              white-space: normal;
              overflow-wrap: anywhere;
            }
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-4px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </section>

        <section id="trust" style={{ marginTop: 64 }}>
          <SectionHead eyebrow={data.trust.eyebrow} title={data.trust.title} lede={data.trust.description} blue />
          <div style={{ marginTop: 24 }}>
            <ClientLogosMarquee />
          </div>
        </section>

        <section id="faq" style={{ marginTop: 64 }}>
          <SectionHead eyebrow={data.faq.eyebrow} title={data.faq.title} blue />
          <FAQList items={data.faq.faqs} />
        </section>

        {guideSlot}

        <section id="abschluss" style={{ marginTop: 64 }}>
          <SectionHead
            eyebrow={data.closing.eyebrow}
            title={data.closing.title}
            lede={data.closing.description}
            blue
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 18, marginTop: 24 }}>
            <WhitepaperDownloadForm
              title={data.whitepaperCta.title}
              description={data.whitepaperCta.description}
              webhookSource="ki_governance_whitepaper_lp"
            />
            <ExpertQuote
              quote={data.closingContact.quote}
              author={data.closingContact.author}
              role={data.closingContact.role}
              phone={data.closingContact.phone}
              imagePath={data.closingContact.imagePath}
              ctaText={data.closingContact.ctaText}
              ctaHref={data.closingContact.ctaHref}
              authorHref={data.closingContact.authorHref}
              authorLinkedIn={data.closingContact.authorLinkedIn}
            />
          </div>
        </section>
      </ArticleLayout>

      <FooterPublic />
    </>
  );
}
